import { useEffect, useState } from "react";
import { Map } from "@vis.gl/react-google-maps";
import { SquareMousePointer } from "lucide-react";
import { ProtectedRoute } from "@/components/protected-route";
import type { Task, Trip } from "@/api.types.v2";
import { TasksSelector } from "./tasks-selector";
import { AllTripsDirections } from "@/features/trips/all-trips-directions";
import { TripRouteMarkers } from "@/features/trips/trip-route-markers";
import {
  tripIsVisible,
  useShortcutsStore,
} from "@/screens/dashboard/shortcuts.store";
import {
  DepotMarkers,
  OperatorMarkers,
  TaskClusterMarkers,
  TripWaypointMarkers,
} from "./markers/markers";
import { CommandBar } from "./command-bar";
import { KpiStrip } from "./kpi-strip";
import { OperatorRail } from "./operator-rail";
import { AssignPanel } from "./assign-panel";
import { WaypointDetail } from "./waypoint-detail";
import { Lookup } from "./lookup";
import { TripFocusModal } from "./trip-focus-modal";
import { AssignDriverDialog } from "./assign-driver-dialog";
import { MapControls } from "./map-controls";
import type { DerivedStop } from "./trip-stops";
import "./dispatch-v2.css";

const DEFAULT_CENTER = { lat: 43.6532, lng: -79.3832 };
const OPERATOR_RAIL_WIDTH = 364;
const COLLAPSED_RAIL_WIDTH = 48;
const DETAIL_PANEL_WIDTH = 372;

type Panel =
  | { kind: "assign"; tasks: Task[] }
  | { kind: "waypoint"; trip: Trip; stop: DerivedStop }
  | { kind: "lookup" }
  | null;

function DispatchV2Content() {
  const [zoomLevel, setZoomLevel] = useState(13);
  const [railCollapsed, setRailCollapsed] = useState(false);
  const [modalTrip, setModalTrip] = useState<Trip | null>(null);
  const [assignDriverTrip, setAssignDriverTrip] = useState<Trip | null>(null);
  const [panel, setPanel] = useState<Panel>(null);

  const isSelectingMode = useShortcutsStore((s) => s.isSelectingMode);
  const tripFilter = useShortcutsStore((s) => s.tripFilter);
  const setIsSelectingMode = useShortcutsStore((s) => s.setIsSelectingMode);
  const toggleSelectingMode = useShortcutsStore((s) => s.toggleSelectingMode);

  const handleSelectionComplete = (
    _: google.maps.LatLngBounds,
    tasks: Task[]
  ) => {
    setIsSelectingMode(false);
    if (tasks.length > 0) setPanel({ kind: "assign", tasks });
  };

  useEffect(() => {
    if (panel?.kind !== "waypoint") return;
    if (!tripIsVisible(tripFilter, panel.trip.id)) setPanel(null);
  }, [panel, tripFilter]);

  // Layer + selection keyboard shortcuts (P/D/C/U/W/O/T · S), matching the
  // legacy route-planning muscle memory. Ignored while typing in a field.
  useEffect(() => {
    const s = useShortcutsStore.getState();
    const onKey = (e: KeyboardEvent) => {
      const el = e.target;
      if (
        el instanceof HTMLElement &&
        (el.tagName === "INPUT" ||
          el.tagName === "TEXTAREA" ||
          el.isContentEditable)
      ) {
        return;
      }
      switch (e.key) {
        case "P":
          s.setShowPickups(!useShortcutsStore.getState().showPickups);
          break;
        case "D":
          s.setShowDeliveries(!useShortcutsStore.getState().showDeliveries);
          break;
        case "C":
          s.setShowCarrierDropoffs(
            !useShortcutsStore.getState().showCarrierDropoffs
          );
          break;
        case "U":
          s.setShowReturns(!useShortcutsStore.getState().showReturns);
          break;
        case "W":
          s.setShowDepots(!useShortcutsStore.getState().showDepots);
          break;
        case "O":
          s.setShowOperators(!useShortcutsStore.getState().showOperators);
          break;
        case "T":
          s.toggleAllTrips();
          break;
        case "S":
          s.toggleSelectingMode();
          break;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="dv2 fixed inset-0" style={{ background: "#eceaf3" }}>
      {/* base map layer — fills the viewport so it gets a stable size at mount
          (a nested flex child does not, and Google Maps fails to initialise).
          Chrome floats on top; the rail/panels are opaque, the centre gap
          shows the map. */}
      <div className="absolute inset-0 z-0">
        <Map
          defaultCenter={DEFAULT_CENTER}
          defaultZoom={13}
          gestureHandling="greedy"
          disableDefaultUI
          mapId="3667d2e0a1e93d2b"
          style={{ width: "100%", height: "100%" }}
          onZoomChanged={(e) => setZoomLevel(e.detail.zoom)}
        >
          <AllTripsDirections />
          <TripRouteMarkers zoomLevel={zoomLevel} />
          <DepotMarkers zoomLevel={zoomLevel} />
          <TaskClusterMarkers
            zoomLevel={zoomLevel}
            onClickCluster={(tasks) => setPanel({ kind: "assign", tasks })}
          />
          <TripWaypointMarkers
            zoomLevel={zoomLevel}
            leftInset={
              railCollapsed ? COLLAPSED_RAIL_WIDTH : OPERATOR_RAIL_WIDTH
            }
            rightInset={panel ? DETAIL_PANEL_WIDTH : 0}
            topInset={110}
            onClickStop={(trip, stop) =>
              setPanel({ kind: "waypoint", trip, stop })
            }
          />
          <OperatorMarkers zoomLevel={zoomLevel} />

          {isSelectingMode && (
            <TasksSelector onSelectionComplete={handleSelectionComplete} />
          )}
        </Map>
      </div>

      {/* chrome overlay — pointer-events pass through to the map except on
          opaque chrome that re-enables them. */}
      <div className="pointer-events-none absolute inset-0 z-10 flex flex-col">
        <div className="pointer-events-auto">
          <CommandBar
            lookupActive={panel?.kind === "lookup"}
            onToggleLookup={() =>
              setPanel((p) =>
                p?.kind === "lookup" ? null : { kind: "lookup" }
              )
            }
          />
          <KpiStrip />
        </div>

        <div className="relative flex min-h-0 flex-1">
          {/* operator rail */}
          <aside
            className="pointer-events-auto shrink-0 border-r border-zinc-200 bg-zinc-50 transition-[width]"
            style={{
              width: railCollapsed ? COLLAPSED_RAIL_WIDTH : OPERATOR_RAIL_WIDTH,
            }}
          >
            <OperatorRail
              collapsed={railCollapsed}
              onToggleCollapsed={() => setRailCollapsed((v) => !v)}
              onOpenTrip={setModalTrip}
              onOpenStop={(trip, stop) =>
                setPanel({ kind: "waypoint", trip, stop })
              }
              onAssignDriver={setAssignDriverTrip}
            />
          </aside>

          {/* map window (transparent — the fixed map shows through) */}
          <main className="relative min-w-0 flex-1">
            <div className="pointer-events-auto absolute left-3 top-3 z-20 flex items-center gap-1 rounded-xl bg-white/95 p-1 shadow-card">
              <button
                onClick={toggleSelectingMode}
                className={
                  "inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-lg px-3 text-[12.5px] font-semibold transition-colors " +
                  (isSelectingMode
                    ? "bg-indigo-600 text-white"
                    : "text-zinc-700 hover:bg-zinc-100")
                }
              >
                <SquareMousePointer className="h-[15px] w-[15px]" /> Box-select
                <span
                  className={
                    "rounded px-1 text-[10px] font-bold " +
                    (isSelectingMode
                      ? "bg-white/25"
                      : "bg-zinc-100 text-zinc-500")
                  }
                >
                  S
                </span>
              </button>
            </div>

            <MapControls />
          </main>

          {/* contextual right panel */}
          {panel && (
            <aside
              className={
                "pointer-events-auto border-l border-zinc-200 bg-white " +
                (panel.kind === "assign"
                  ? "absolute inset-0 z-20 min-w-0 lg:static lg:w-[372px] lg:shrink-0"
                  : "w-[372px] shrink-0")
              }
            >
              {panel.kind === "assign" && (
                <AssignPanel
                  tasks={panel.tasks}
                  onClose={() => setPanel(null)}
                />
              )}
              {panel.kind === "waypoint" && (
                <WaypointDetail
                  trip={panel.trip}
                  stop={panel.stop}
                  onClose={() => setPanel(null)}
                  onOpenTrip={setModalTrip}
                />
              )}
              {panel.kind === "lookup" && (
                <Lookup
                  onClose={() => setPanel(null)}
                  onOpenTrip={setModalTrip}
                />
              )}
            </aside>
          )}
        </div>
      </div>

      <TripFocusModal
        trip={modalTrip}
        open={!!modalTrip}
        onOpenChange={(open) => !open && setModalTrip(null)}
      />
      <AssignDriverDialog
        trip={assignDriverTrip}
        open={!!assignDriverTrip}
        onOpenChange={(open) => !open && setAssignDriverTrip(null)}
      />
    </div>
  );
}

export default function DispatchV2Screen() {
  return (
    <ProtectedRoute>
      <DispatchV2Content />
    </ProtectedRoute>
  );
}
